import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '@/lib/auth';
// Note: Using backend API instead of Supabase direct calls for auth compatibility

// Redirect component for legacy /course-instances/new URL
function CourseInstanceNewRedirect() {
  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();
  return <Navigate to={`/course-instances${queryString ? `?${queryString}` : ''}`} replace />;
}

// Wrapper for QuestionReview to fetch study plan details
function QuestionReviewWrapper() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [studyPlan, setStudyPlan] = useState<{ id: string; title: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadStudyPlan() {
      if (!id) {
        setError('Ingen studieplan-ID angitt');
        setLoading(false);
        return;
      }
      try {
        // Use backend API instead of Supabase direct (auth.uid() doesn't work with custom auth)
        const token = localStorage.getItem('auth_token');
        const userStr = localStorage.getItem('auth_user');
        const user = userStr ? JSON.parse(userStr) : null;

        if (!token || !user) {
          setError('Du må være logget inn for å se denne siden');
          setLoading(false);
          return;
        }

        const response = await fetch('/api/study-plans/get', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            user_id: user.id,
            study_plan_id: id
          })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          console.error('QuestionReviewWrapper: Feil ved lasting av studieplan:', data.error);
          setError(data.error || 'Studieplanen ble ikke funnet eller du har ikke tilgang');
        } else {
          setStudyPlan({ id: data.study_plan.id, title: data.study_plan.title });
        }
      } catch (err: any) {
        console.error('QuestionReviewWrapper: Uventet feil:', err);
        setError(`Nettverksfeil: Kunne ikke koble til databasen. Prøv å laste siden på nytt.`);
      }
      setLoading(false);
    }
    loadStudyPlan();
  }, [id]);

  if (loading) {
    return <div className="flex items-center justify-center h-64">Laster...</div>;
  }

  if (error || !studyPlan) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
          {error || 'Studieplanen ble ikke funnet'}
        </div>
        <button
          onClick={() => navigate('/study-plans')}
          className="btn btn-secondary"
        >
          ← Tilbake til studieplaner
        </button>
      </div>
    );
  }

  return (
    <QuestionReview
      studyPlanId={studyPlan.id}
      studyPlanTitle={studyPlan.title}
      onBack={() => navigate(`/study-plans/${studyPlan.id}`)}
    />
  );
}

// Wrapper for LicenseConfirmation to fetch study plan details
function LicenseConfirmationWrapper() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [studyPlan, setStudyPlan] = useState<{ id: string; title: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadStudyPlan() {
      if (!id) {
        setError('Ingen studieplan-ID angitt');
        setLoading(false);
        return;
      }
      try {
        // Use backend API instead of Supabase direct (auth.uid() doesn't work with custom auth)
        const token = localStorage.getItem('auth_token');
        const userStr = localStorage.getItem('auth_user');
        const user = userStr ? JSON.parse(userStr) : null;

        if (!token || !user) {
          setError('Du må være logget inn for å se denne siden');
          setLoading(false);
          return;
        }

        const response = await fetch('/api/study-plans/get', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            user_id: user.id,
            study_plan_id: id
          })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          console.error('LicenseConfirmationWrapper: Feil ved lasting av studieplan:', data.error);
          setError(data.error || 'Studieplanen ble ikke funnet eller du har ikke tilgang');
        } else {
          setStudyPlan({ id: data.study_plan.id, title: data.study_plan.title });
        }
      } catch (err: any) {
        console.error('LicenseConfirmationWrapper: Uventet feil:', err);
        setError(`Nettverksfeil: Kunne ikke koble til databasen. Prøv å laste siden på nytt.`);
      }
      setLoading(false);
    }
    loadStudyPlan();
  }, [id]);

  if (loading) {
    return <div className="flex items-center justify-center h-64">Laster...</div>;
  }

  if (error || !studyPlan) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
          {error || 'Studieplanen ble ikke funnet'}
        </div>
        <button
          onClick={() => navigate('/study-plans')}
          className="btn btn-secondary"
        >
          ← Tilbake til studieplaner
        </button>
      </div>
    );
  }

  return (
    <LicenseConfirmation
      studyPlanId={studyPlan.id}
      studyPlanTitle={studyPlan.title}
      onBack={() => navigate(`/study-plans/${studyPlan.id}`)}
    />
  );
}
import { getCurrentUser } from './lib/auth';
import type { User } from './types';

// Components (will be implemented)
import Layout from './components/Layout';
import AuthForm from './components/AuthForm';
import ForgotPasswordForm from './components/ForgotPasswordForm';
import ResetPasswordForm from './components/ResetPasswordForm';
import VerifyEmailPage from './components/VerifyEmailPage';
import Dashboard from './components/Dashboard';
import StudyPlansList from './components/StudyPlansList';
import StudyPlanForm from './components/StudyPlanForm';
import StudyPlanReview from './components/StudyPlanReview';
import PublicVerification from './components/PublicVerification';
import PublicVerify from './components/PublicVerify';
import DeviceManagement from './components/DeviceManagement';
import StudyAssociationManagement from './components/StudyAssociationManagement';
import Settings from './components/Settings';
import SystemSettings from './components/SystemSettings';
import Account from './components/settings/Account';
import Documentation from './components/settings/Documentation';
import CuratedSourceRequests from './components/settings/CuratedSourceRequests';
import PendingDomainsAdmin from './components/PendingDomainsAdmin';
import PendingMemberAdminRequests from './components/PendingMemberAdminRequests';
import APISettings from './components/settings/APISettings';
import ActivityLog from './components/settings/ActivityLog';
import TokenUsage from './components/settings/TokenUsage';
import ApiAccessManagement from './components/settings/ApiAccessManagement';
import MemberSettings from './components/MemberSettings';
import MemberIntegrations from './components/MemberIntegrations';
import IntegrationManagement from './components/IntegrationManagement';
import IntegrationApprovalReview from './components/IntegrationApprovalReview';
import LmsCoursesPage from './components/LmsCoursesPage';
import LmsIntegrationGuidePage from './components/LmsIntegrationGuidePage';
import AdminSettings from './components/AdminSettings';
import ExamCreator from './components/ExamCreator';
import ExamDetail from './components/ExamDetail';
import ExamReview from './components/ExamReview';
import ExamTaking from './components/ExamTaking';
import SensorDashboard from './components/SensorDashboard';
import SensorGrading from './components/SensorGrading';
import MembersAndUsers from './components/MembersAndUsers';
import QualityAssuranceOnboarding from './components/QualityAssuranceOnboarding';
import SensorManagementForMember from './components/SensorManagementForMember';
import CourseInstanceDetail from './components/CourseInstanceDetail';
import SensorManualEntry from './components/SensorManualEntry';
import UserProfile from './components/UserProfile';
import CoursesAndExams from './components/CoursesAndExams';
import QuestionReview from './components/QuestionReview';
import LicenseConfirmation from './components/LicenseConfirmation';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Check if we're on the public verification site
  const isPublicVerifySite = window.location.hostname === 'www2.kompetanseattest.no' ||
                              window.location.hostname === 'www.kompetanseattest.no';

  // Check if we're on a public route that doesn't need auth
  const isPublicRoute = location.pathname.startsWith('/verify/') ||
                        location.pathname.startsWith('/exam/') ||
                        location.pathname.startsWith('/take-exam/') ||
                        location.pathname.startsWith('/start-exam/') ||
                        location.pathname === '/verify-email' ||
                        location.pathname === '/login' ||
                        location.pathname === '/register' ||
                        location.pathname === '/forgot-password' ||
                        location.pathname === '/reset-password';

  useEffect(() => {
    // Skip auth check for public routes
    if (isPublicRoute) {
      setLoading(false);
      return;
    }

    getCurrentUser().then((currentUser) => {
      setUser(currentUser);
      setLoading(false);
    }).catch(() => {
      setUser(null);
      setLoading(false);
    });
  }, [location.pathname, isPublicRoute]);

  // Show loading only for non-public routes
  // If we're on the public verification site, show only public routes
  if (isPublicVerifySite) {
    return (
      <Routes>
        <Route path="/" element={<PublicVerify />} />
        <Route path="/verify/:approvalLink" element={<PublicVerification />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Public exam routes - always accessible without auth
  if (location.pathname.startsWith('/exam/') || location.pathname.startsWith('/take-exam/') || location.pathname.startsWith('/start-exam/')) {
    return (
      <Routes>
        <Route path="/exam/invite/:invitationToken" element={<ExamTaking />} />
        <Route path="/exam/:publicLink" element={<ExamTaking />} />
        <Route path="/take-exam/:uniqueCandidateId" element={<ExamTaking />} />
        <Route path="/start-exam/:candidateCode" element={<ExamTaking />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  if (loading && !isPublicRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kf-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Laster...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/verify/:approvalLink" element={<PublicVerification />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/login" element={<AuthForm mode="login" />} />
        <Route path="/register" element={<AuthForm mode="register" />} />
        <Route path="/forgot-password" element={<ForgotPasswordForm />} />
        <Route path="/reset-password" element={<ResetPasswordForm />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout user={user}>
      <OnboardingGuard>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/study-plans" element={<StudyPlansList />} />
        <Route path="/study-plans/new" element={<StudyPlanForm />} />
        <Route path="/study-plans/:id/edit" element={<StudyPlanForm />} />
        <Route path="/study-plans/:id" element={<StudyPlanReview />} />
        <Route path="/study-plans/:id/questions" element={<QuestionReviewWrapper />} />
        <Route path="/study-plans/:id/licenses" element={<LicenseConfirmationWrapper />} />
        {/* Consolidated Courses and Exams page with tabs */}
        <Route path="/courses-and-exams" element={<CoursesAndExams />} />
        {/* Legacy redirects - redirect old URLs to new tabbed view */}
        <Route path="/exams" element={<Navigate to="/courses-and-exams?tab=exams" replace />} />
        <Route path="/exams/create" element={<ExamCreator />} />
        <Route path="/exams/edit/:id" element={<ExamCreator />} />
        <Route path="/exams/review/:sessionId" element={<ExamReview />} />
        <Route path="/exams/:id" element={<ExamDetail />} />
        <Route path="/users" element={<Navigate to="/members" replace />} />
        <Route path="/members" element={<MembersAndUsers />} />
        <Route path="/members/:memberId/sensors" element={<SensorManagementForMember />} />
        <Route path="/sensors" element={<Navigate to="/courses-and-exams?tab=sensors" replace />} />
        <Route path="/literature" element={<Navigate to="/study-plans?tab=literature" replace />} />
        <Route path="/onboarding/quality-assurance" element={<QualityAssuranceOnboarding />} />
        <Route path="/study-associations" element={<StudyAssociationManagement />} />
        <Route path="/admin/integration-requests" element={<IntegrationApprovalReview />} />
        <Route path="/lms-courses" element={<LmsCoursesPage />} />
        <Route path="/help/integrations" element={<LmsIntegrationGuidePage />} />
        <Route path="/help/integrations/:lmsType" element={<LmsIntegrationGuidePage />} />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/devices" element={<Navigate to="/profile" replace />} />
        <Route path="/settings" element={<Settings />}>
          <Route path="email" element={<SystemSettings />} />
          <Route path="ai-prompts" element={<SystemSettings />} />
          <Route path="api" element={<APISettings />} />
          <Route path="api-access" element={<ApiAccessManagement />} />
          <Route path="integrations" element={<IntegrationManagement />} />
          <Route path="email-domains" element={<PendingDomainsAdmin />} />
          <Route path="member-admin-requests" element={<PendingMemberAdminRequests />} />
          <Route path="legal" element={<SystemSettings />} />
          <Route path="backup" element={<SystemSettings />} />
          <Route path="esco" element={<SystemSettings />} />
          <Route path="activity-log" element={<ActivityLog />} />
          <Route path="token-usage" element={<TokenUsage />} />
          <Route path="devices" element={<DeviceManagement />} />
          <Route path="account" element={<Account />} />
          <Route path="documentation" element={<Documentation />} />
        </Route>
        {/* Member Settings - for member role */}
        <Route path="/member-settings" element={<MemberSettings />}>
          <Route index element={<Navigate to="/member-settings/integrations" replace />} />
          <Route path="integrations" element={<MemberIntegrations />} />
          <Route path="devices" element={<DeviceManagement />} />
          <Route path="account" element={<Account />} />
          <Route path="documentation" element={<Documentation />} />
        </Route>
        {/* Admin Settings - for studieforbund admin */}
        <Route path="/admin-settings" element={<AdminSettings />}>
          <Route index element={<Navigate to="/admin-settings/integrations" replace />} />
          <Route path="integrations" element={<IntegrationManagement />} />
          <Route path="literature-requests" element={<CuratedSourceRequests />} />
          <Route path="email-domains" element={<PendingDomainsAdmin />} />
          <Route path="member-admin-requests" element={<PendingMemberAdminRequests />} />
          <Route path="devices" element={<DeviceManagement />} />
          <Route path="account" element={<Account />} />
          <Route path="documentation" element={<Documentation />} />
        </Route>
        {/* Redirect old /integrations to admin-settings */}
        <Route path="/integrations" element={<Navigate to="/admin-settings/integrations" replace />} />
        <Route path="/api-credentials" element={<Navigate to="/members" replace />} />
        <Route path="/course-instances" element={<Navigate to="/courses-and-exams?tab=courses" replace />} />
        <Route path="/course-instances/new" element={<CourseInstanceNewRedirect />} />
        <Route path="/course-instances/:courseId" element={<CourseInstanceDetail />} />
        <Route path="/sensor/dashboard" element={<SensorDashboard />} />
        <Route path="/sensor/course/:courseId" element={<SensorGrading />} />
        <Route path="/sensor/manual-entry/:sessionId" element={<SensorManualEntry />} />
        <Route path="/verify/:approvalLink" element={<PublicVerification />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </OnboardingGuard>
    </Layout>
  );
}

/**
 * Routing-guard som sjekker om brukeren må gjennom onboarding.
 * Redirecter til /onboarding/quality-assurance hvis required=true,
 * unntatt på selve onboarding-siden.
 */
function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith('/onboarding')) {
      setChecked(true);
      return;
    }
    (async () => {
      try {
        const res = await authenticatedFetch('/api/onboarding/status');
        const data = await res.json();
        if (data.success && data.required) {
          navigate('/onboarding/quality-assurance', { replace: true });
        }
      } catch {
        // Ikke-blokkerende — vis normal app hvis status-sjekk feiler
      } finally {
        setChecked(true);
      }
    })();
  }, [location.pathname]);

  if (!checked) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kf-primary"></div>
      </div>
    );
  }
  return <>{children}</>;
}

export default App;
